/*
 * Copyright 2022 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  WbBreadcrumb,
  WbBreadcrumbs,
  WbHeader,
  SortingState,
} from '@agilelab/plugin-wb-platform';
import {
  Content,
  EmptyState,
  ErrorPanel,
  Progress,
} from '@mainstage/core-components';
import { SubRouteRef, useApi, useRouteRef } from '@mainstage/core-plugin-api';
import { useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { scaffolderApiRef } from '../../api';
import { ListTasksTable } from './ListTasksTable';
import { useLocation } from 'react-router-dom';
import {
  scaffolderTaskRouteRef,
  taskListRouteRef as platformSettingsTasksRouteRef,
  templateIndexRouteRef,
} from '../../routes';
import { WbPage } from '@agilelab/plugin-wb-platform';

export interface MyTaskPageProps {
  initiallySelectedFilter?: 'owned' | 'all';
  elementRouteRef?: SubRouteRef;
}

const ListTaskPageContent = (props: MyTaskPageProps) => {
  const { initiallySelectedFilter = 'all' } = props;
  const scaffolderApi = useApi(scaffolderApiRef);
  const { elementRouteRef = scaffolderTaskRouteRef } = props;

  const [ownerFilter, setOwnerFilter] = useState(initiallySelectedFilter);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    owned: undefined as undefined | boolean,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  // Map frontend columns to database column names
  const columnToDbFieldMap: Record<string, string> = {
    id: 'id',
    createdAt: 'created_at',
    status: 'status',
  };

  const { value, loading, error } = useAsync(() => {
    if (scaffolderApi.listTasks) {
      // Backend always handles pagination (DB-level when no filters, in-memory when filters active)
      const paginationOptions = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };

      const orderOptions =
        sorting.length > 0
          ? {
              order: sorting.reduce((acc, sort) => {
                const field = columnToDbFieldMap[sort.id];
                if (field) {
                  acc.push({
                    field,
                    order: sort.desc ? ('desc' as const) : ('asc' as const),
                  });
                }
                return acc;
              }, [] as { field: string; order: 'asc' | 'desc' }[]),
            }
          : {};

      return scaffolderApi.listTasks({
        filterByOwnership: ownerFilter,
        ...paginationOptions,
        ...orderOptions,
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.type ? { templateType: filters.type } : {}),
        ...(filters.owned !== undefined ? { owned: filters.owned } : {}),
      });
    }

    return Promise.resolve({ tasks: [], totalTasks: 0 });
  }, [
    scaffolderApi,
    ownerFilter,
    page,
    rowsPerPage,
    sorting,
    filters.search,
    filters.type,
    filters.owned,
  ]);

  const handleOwnerFilterChange = (newOwnerFilter: 'owned' | 'all') => {
    setOwnerFilter(newOwnerFilter);
    setPage(0);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (search: string) => {
    setFilters(prev => ({ ...prev, search }));
    setPage(0);
  };

  const handleTypeChange = (type: string) => {
    setFilters(prev => ({ ...prev, type }));
    setPage(0);
  };

  const handleOwnedChange = (owned: boolean | undefined) => {
    setFilters(prev => ({ ...prev, owned }));
    setPage(0);
  };

  const handleSortingChange = (
    updaterOrValue: SortingState | ((old: SortingState) => SortingState),
  ) => {
    setSorting(updaterOrValue);
    setPage(0);
  };

  const isInitialLoading = loading && !value;

  if (isInitialLoading) {
    return <Progress />;
  }

  if (error && !value) {
    return (
      <>
        <ErrorPanel error={error} />
        <EmptyState
          missing="info"
          title="No information to display"
          description="There is no Tasks or there was an issue communicating with backend."
        />
      </>
    );
  }

  return (
    <ListTasksTable
      tasks={value?.tasks || []}
      ownerFilter={ownerFilter}
      setOwnerFilter={handleOwnerFilterChange}
      elementRouteRef={elementRouteRef}
      filters={filters}
      onSearchChange={handleSearchChange}
      onTypeChange={handleTypeChange}
      onOwnedChange={handleOwnedChange}
      page={page}
      rowsPerPage={rowsPerPage}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
      totalTasks={value?.totalTasks ?? value?.tasks.length ?? 0}
      isServerPagination
      isFetching={loading}
      sorting={sorting}
      onSortingChange={handleSortingChange}
    />
  );
};

export const ListTasksPage = (props: MyTaskPageProps) => {
  const adminRoute = useRouteRef(platformSettingsTasksRouteRef);
  const currentRoute = useLocation().pathname;

  const adminTemplateIndexRoute = useRouteRef(templateIndexRouteRef);

  return (
    <WbPage themeId="home">
      <WbHeader
        pageTitleOverride="Tasks List"
        title="Tasks List"
        breadcrumbs={
          currentRoute === adminRoute() ? (
            <WbBreadcrumbs aria-label="breadcrumb">
              <WbBreadcrumb>Administration</WbBreadcrumb>
              <WbBreadcrumb to={adminTemplateIndexRoute()}>
                Templates
              </WbBreadcrumb>
              <WbBreadcrumb>Tasks List</WbBreadcrumb>
            </WbBreadcrumbs>
          ) : (
            <WbBreadcrumbs aria-label="breadcrumb">
              <WbBreadcrumb>Builder</WbBreadcrumb>
              <WbBreadcrumb to="/templates">Templates</WbBreadcrumb>
              <WbBreadcrumb>Tasks List</WbBreadcrumb>
            </WbBreadcrumbs>
          )
        }
      />
      <Content>
        <ListTaskPageContent {...props} />
      </Content>
    </WbPage>
  );
};
